import React, { useState, useMemo } from 'react';
import { Card, Descriptions, Table, Tag, Button, Space, Modal, Form, Input, Alert, message } from 'antd';
import { useTranslation } from 'react-i18next';
import { Link, useLoaderData, useNavigate } from 'react-router-dom';
import { CheckCircleOutlined, LockOutlined } from '@ant-design/icons';
import ListPageHeader from '@shared/ListPageHeader';
import CustomAttributesTags from '@components/shared/inventory_attribute_definitions/CustomAttributesTags';
import { useInventoryAttributeDefinitions } from '@hooks/orders/useInventoryAttributeDefinitions';
import { formatDateTime, formatDate } from '@utils/dateTime';

export default function InventoryHoldShow({ service, role = 'operator', canRelease = false }) {
  const inventoryHold = useLoaderData();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [releaseModalVisible, setReleaseModalVisible] = useState(false);
  const [form] = Form.useForm();

  const isActive = !inventoryHold.released_at;

  const handleRelease = async () => {
    try {
      setLoading(true);
      const values = await form.validateFields();
      const response = await service.batchRelease([inventoryHold.id], values.release_note);
      if (response.success) {
        message.success(t('shared.inventory_holds.release_success'));
        setReleaseModalVisible(false);
        navigate(0);
      } else {
        message.error(response.message || t('common.message.operation_failed'));
      }
    } catch (error) {
      if (error.errorFields) return;
      console.error(error);
      message.error(t('common.message.operation_failed'));
    } finally {
      setLoading(false);
    }
  };

  // Map attribute name → label for the shared CustomAttributesTags component.
  // Falls back to raw key in the tag if a definition isn't present for the merchant.
  const attributeDefinitions = useInventoryAttributeDefinitions(role, inventoryHold.merchant?.id);
  const attributeLabelByName = useMemo(
    () => Object.fromEntries(attributeDefinitions.map(def => [def.name, def.label])),
    [attributeDefinitions],
  );

  const columns = [
    {
      title: t('models.inventory_hold.product'),
      key: 'product',
      render: (_, r) => (
        <Link to={`/products/${r.inventory.product_id}`}>
          <div>{r.inventory.product_sku}</div>
          <div style={{ fontSize: 12, color: '#666' }}>{r.inventory.product_name}</div>
        </Link>
      ),
    },
    {
      title: t('models.inventory_type._'),
      key: 'inventory_type',
      render: (_, r) => r.inventory?.inventory_type_name || '-',
    },
    {
      title: t('models.inventory.batch'),
      dataIndex: ['inventory', 'batch'],
      key: 'batch',
      render: text => text || '-',
    },
    {
      title: t('models.inventory.manufacturing_date'),
      dataIndex: ['inventory', 'manufacturing_date'],
      key: 'manufacturing_date',
      render: text => text ? formatDate(text) : '-',
    },
    {
      title: t('models.inventory.expiration_date'),
      dataIndex: ['inventory', 'expiration_date'],
      key: 'expiration_date',
      render: text => text ? formatDate(text) : '-',
    },
    {
      title: t('models.inventory.custom_attributes'),
      dataIndex: ['inventory', 'custom_attributes'],
      key: 'custom_attributes',
      render: attrs => (
        <CustomAttributesTags
          attributes={attrs}
          attributeLabelByName={attributeLabelByName}
        />
      ),
    },
    {
      title: t('models.inventory_hold.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      align: 'right',
      render: qty => (
        <Space size={4}>
          <LockOutlined style={{ color: '#faad14' }} />
          <span style={{ color: '#faad14', fontWeight: 'bold' }}>{qty}</span>
        </Space>
      ),
    },
  ];

  const statusTag = isActive
    ? <Tag color="warning">{t('shared.inventory_holds.status.active')}</Tag>
    : <Tag color="default">{t('shared.inventory_holds.status.released')}</Tag>;

  return (
    <div style={{ maxWidth: 1200, margin: '0 auto' }}>
      <ListPageHeader
        basePath={`/${role}`}
        items={['stocks', 'stocks.inventory_holds', { label: inventoryHold.number }]}
        title={
          <Space>
            {inventoryHold.number}
            {statusTag}
          </Space>
        }
        actions={
          isActive && canRelease ? (
            <Button
              type="primary"
              icon={<CheckCircleOutlined />}
              onClick={() => setReleaseModalVisible(true)}
            >
              {t('shared.inventory_holds.release')}
            </Button>
          ) : undefined
        }
      />

      <Card bordered={false} style={{ marginBottom: 24 }}>
        <Descriptions bordered column={3}>
          <Descriptions.Item label={t('models.inventory_hold.reason')}>
            <Tag color="warning">{t(`enums.inventory_hold.reason.${inventoryHold.reason}`)}</Tag>
          </Descriptions.Item>
          <Descriptions.Item label={t('models.inventory_hold.expires_on')}>
            {inventoryHold.expires_on ? formatDate(inventoryHold.expires_on) : '-'}
          </Descriptions.Item>
          <Descriptions.Item label={t('models.inventory_hold.held_by')}>
            {inventoryHold.held_by}
          </Descriptions.Item>
          <Descriptions.Item label={t('common.created_at')}>
            {formatDateTime(inventoryHold.created_at)}
          </Descriptions.Item>
          {inventoryHold.released_at && (
            <>
              <Descriptions.Item label={t('shared.inventory_holds.released_at')}>
                {formatDateTime(inventoryHold.released_at)}
              </Descriptions.Item>
              <Descriptions.Item label={t('shared.inventory_holds.released_by')}>
                {inventoryHold.released_by || '-'}
              </Descriptions.Item>
            </>
          )}
          <Descriptions.Item label={t('models.inventory_hold.note')} span={inventoryHold.released_at ? 3 : 2}>
            {inventoryHold.note || '-'}
          </Descriptions.Item>
          {inventoryHold.release_note && (
            <Descriptions.Item label={t('shared.inventory_holds.release_note_label')} span={3}>
              {inventoryHold.release_note}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      <Card title={t('shared.inventory_holds.items')} bordered={false}>
        <Table
          columns={columns}
          dataSource={inventoryHold.items}
          rowKey="id"
          pagination={false}
          size="small"
        />
      </Card>

      {/* 釋放 Modal（含備註輸入） */}
      <Modal
        title={
          <span>
            <CheckCircleOutlined style={{ color: '#52c41a', marginRight: 8 }} />
            {t('shared.inventory_holds.release')}
          </span>
        }
        open={releaseModalVisible}
        onOk={handleRelease}
        onCancel={() => { setReleaseModalVisible(false); form.resetFields(); }}
        confirmLoading={loading}
        okText={t('common.button.confirm')}
        cancelText={t('common.button.cancel')}
      >
        <Alert
          message={t('shared.inventory_holds.release_confirm', { number: inventoryHold.number })}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <Form form={form} layout="vertical">
          <Form.Item name="release_note" label={t('shared.inventory_holds.release_note_label')}>
            <Input.TextArea
              rows={4}
              placeholder={t('shared.inventory_holds.release_note_placeholder')}
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
